import { Injectable } from '@angular/core';
import { ExamQuestionGroupInput } from '@generated/base-types';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { AssetService } from '@new/common/services/asset.service';
import { parseError } from '@new/common/utils/error-parser';
import {
  assertIsDefined,
  isDefined
} from '@new/common/utils/type-guards/is-defined';
import {
  Booklet,
  BookletType,
  ExamQuestionGroup,
  LockState
} from '@new/pools/lib/types';
import { PresortExamQuestionsGQL } from '@new/pools/pool/lib/services/exam/presort-exam-questions.generated';
import { TranslateService } from '@ngx-translate/core';
import type { StateContext } from '@ngxs/store';
import { Action, State, Store } from '@ngxs/store';
import { catchError, filter, map, Observable, switchMap } from 'rxjs';
import { ToastsService } from '../../../../common/services/toasts.service';
import { DeleteExamQuestionGroupGQL } from '../../lib/services/exam/delete-exam-question-group.generated';
import { LoadExamQuestionsGQL } from '../../lib/services/exam/load-exam-questions.generated';
import { ToggleIcebreakerGQL } from '../../lib/services/exam/toggle-exam-question-group-icebreaker.generated';
import { UpdateEqgPositionsGQL } from '../../lib/services/exam/update-exam-question-group-positions.generated';
import { UpdateExamQuestionGroupVersionGQL } from '../../lib/services/exam/update-question-version.generated';
import { LoadExam } from '../exam/exam.actions';

import { selectPoolId } from '../pool/pool.state.model';
import * as Act from './exam-question-groups.actions';
import {
  examQuestionGroupsStateDefaults,
  ExamQuestionGroupsStateModel
} from './exam-question-groups.state.model';

@State<ExamQuestionGroupsStateModel>({
  name: 'examQuestionGroups',
  defaults: examQuestionGroupsStateDefaults
})
@Injectable()
export class ExamQuestionGroupsState {
  constructor(
    private readonly loadExamQuestionsService: LoadExamQuestionsGQL,
    private readonly updateEQGPositionsService: UpdateEqgPositionsGQL,
    private readonly toggleIcebreakerService: ToggleIcebreakerGQL,
    private readonly presortExamQuestionsService: PresortExamQuestionsGQL,
    private readonly removeExamQuestionService: DeleteExamQuestionGroupGQL,
    private readonly updateExamQuestionVersionService: UpdateExamQuestionGroupVersionGQL,
    private readonly toasts: ToastsService,
    private readonly translate: TranslateService,
    private readonly store: Store,
    private readonly assetService: AssetService
  ) {}

  @Action(Act.LoadExamQuestionGroups)
  public loadExamQuestionGroups(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examId }: Act.LoadExamQuestionGroups
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'loading' } }
    });

    return this.loadExamQuestionsService.fetch({ examId }).pipe(
      takeGraphQLResult(),
      switchMap(res =>
        ctx.dispatch(new Act.LoadExamQuestionGroupsSuccess(res.exam))
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadExamQuestionGroupsFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadExamQuestionGroupsSuccess)
  public loadExamQuestionGroupsSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examQuestions }: Act.LoadExamQuestionGroupsSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: examQuestions,
        query: { state: 'success' }
      }
    });

    const assetIds = [
      ...(examQuestions.imageAssets ?? []),
      ...(examQuestions.videoAssets ?? [])
    ];

    ctx.dispatch(new Act.LoadExamQuestionGroupAssetMetadata(assetIds));
  }

  @Action(Act.LoadExamQuestionGroupsFailure)
  public loadExamQuestionGroupsFailure(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { error }: Act.LoadExamQuestionGroupsFailure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'failure', error } }
    });
  }

  @Action(Act.LoadExamQuestionGroupAssetMetadata)
  public async loadAssetMetadata(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { assetIds }: Act.LoadExamQuestionGroupAssetMetadata
  ): Promise<void> {
    try {
      const assetSize = await this.assetService.getTotalAssetMbSize(assetIds);

      ctx.patchState({ assetSize });
    } catch {
      // If the asset size cannot be calculated, we set it to 0 to avoid displaying incorrect information. I don't want to show an error message here, as this is not a critical failure for the user. The asset size is just additional information and not essential for the main functionality of the application.
      ctx.patchState({ assetSize: 0 });
    }
  }

  @Action(Act.SetSelectedExamQuestionGroupIds)
  public setSelectedIds(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { ids }: Act.SetSelectedExamQuestionGroupIds
  ): void {
    ctx.patchState({ selectedIds: ids });
  }

  @Action(Act.ResetSelectedExamQuestionGroupIds)
  public resetSelectedIds(
    ctx: StateContext<ExamQuestionGroupsStateModel>
  ): void {
    ctx.patchState({ selectedIds: [] });
  }

  @Action(Act.ResetTableState)
  public resetTableState(
    ctx: StateContext<ExamQuestionGroupsStateModel>
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, data: undefined },
      selectedIds: []
    });
  }

  @Action(Act.SetSelectedBooklet)
  public setSelectedBooklet(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { booklet }: Act.SetSelectedBooklet
  ): void {
    ctx.patchState({ selectedBooklet: booklet });
  }

  @Action(Act.RefreshExamQuestionGroups)
  public refresh(ctx: StateContext<ExamQuestionGroupsStateModel>): void {
    const currentData = ctx.getState().items.data;
    if (!currentData) return;

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: {
          ...currentData,
          bookletA: {
            ...currentData.bookletA,
            examQuestionGroups: [...currentData.bookletA.examQuestionGroups]
          },
          bookletB: {
            ...currentData.bookletB,
            examQuestionGroups: [...currentData.bookletB.examQuestionGroups]
          }
        }
      }
    });
  }

  /* Mutation actions */

  @Action(Act.Failure)
  public failure(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { error, validationMessage }: Act.Failure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'failure', error } }
    });

    this.toasts.addWarning(
      validationMessage ?? this.translate.instant('error_modal.title')
    );
  }

  @Action(Act.UpdateExamQuestionGroupPosition)
  public updatePosition(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { oldPosition, newPosition }: Act.UpdateExamQuestionGroupPosition
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const selectedBooklet = ctx.getState().selectedBooklet;
    const data = ctx.getState().items.data;
    const questionGroups =
      selectedBooklet === BookletType.A
        ? data?.bookletA.examQuestionGroups ?? []
        : data?.bookletB.examQuestionGroups ?? [];

    const newPositions = this.moveQuestionGroup(
      oldPosition,
      newPosition,
      questionGroups,
      selectedBooklet
    );

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to update positions');

    return this.updateEQGPositionsService.mutate({ poolId, newPositions }).pipe(
      takeGraphQLResult(),
      map(
        res => res?.updateExamQuestionGroupPositions?.updatedExamQuestionsGroups
      ),
      filter(isDefined),
      switchMap(updatedExamQGs =>
        ctx.dispatch(
          new Act.UpdateExamQuestionGroupPositionSuccess(updatedExamQGs)
        )
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.UpdateExamQuestionGroupPositionSuccess)
  public updatePositionSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    {
      updatedExamQuestionsGroups,
      afterDeletion
    }: Act.UpdateExamQuestionGroupPositionSuccess
  ): void {
    const currentData = ctx.getState().items.data;
    const selectedBooklet = ctx.getState().selectedBooklet;
    const newData = currentData
      ? {
          ...currentData,
          [selectedBooklet === BookletType.A ? Booklet.A : Booklet.B]: {
            ...(selectedBooklet === BookletType.A
              ? currentData.bookletA
              : currentData.bookletB),
            examQuestionGroups: currentData.bookletA.examQuestionGroups
              .map(eqg => {
                const updated = updatedExamQuestionsGroups.find(
                  u => u.id === eqg.id
                );

                return {
                  ...eqg,
                  positionA:
                    updated === undefined ? eqg.positionA : updated.positionA,
                  positionB:
                    updated === undefined ? eqg.positionB : updated.positionB,
                  positionsBookletA:
                    updated === undefined
                      ? eqg.positionsBookletA
                      : updated.positionsBookletA,
                  positionsBookletB:
                    updated === undefined
                      ? eqg.positionsBookletB
                      : updated.positionsBookletB
                };
              })
              .sort((a, b) => {
                const posA =
                  selectedBooklet === BookletType.A ? a.positionA : a.positionB;
                const posB =
                  selectedBooklet === BookletType.A ? b.positionA : b.positionB;

                return posA - posB;
              })
          }
        }
      : undefined;

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: newData,
        mutation: { state: 'success' }
      }
    });

    const toastMessageKey = afterDeletion ? 'destroy' : 'update';
    const resourceKey = afterDeletion
      ? 'common.models.question'
      : 'exam_management.exam_questions.messages.exam_question_position';

    this.toasts.addSuccess(
      this.translate.instant(`toast_success_messages.${toastMessageKey}`, {
        resource: this.translate.instant(resourceKey)
      })
    );
  }

  @Action(Act.ApplySortingOfBookletAToBookletB)
  public applySortingAToB(
    ctx: StateContext<ExamQuestionGroupsStateModel>
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const examQuestionsA =
      ctx.getState().items.data?.bookletA.examQuestionGroups ?? [];

    const newPositions = examQuestionsA.map((group, index) => ({
      id: group.id.toString(),
      positionA: group.positionA,
      positionB: index
    }));

    const rearrangedQuestionGroups = examQuestionsA.map((group, index) => ({
      ...group,
      positionB: index
    }));

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to update positions');

    return this.updateEQGPositionsService.mutate({ poolId, newPositions }).pipe(
      takeGraphQLResult(),
      switchMap(() =>
        ctx.dispatch(
          new Act.ApplySortingOfBookletAToBookletBSuccess(
            rearrangedQuestionGroups
          )
        )
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.ApplySortingOfBookletAToBookletBSuccess)
  public applySortingAToBSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { updatedExamQuestions }: Act.ApplySortingOfBookletAToBookletBSuccess
  ): void {
    const currentData = ctx.getState().items.data;
    const newData = currentData
      ? {
          ...currentData,
          bookletB: {
            ...currentData.bookletB,
            examQuestionGroups: updatedExamQuestions
          }
        }
      : undefined;

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: newData,
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.exam_questions_sorting')
      })
    );
  }

  @Action(Act.SetIcebreaker)
  public setIcebreaker(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    action: Act.SetIcebreaker
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.toggleIcebreakerService
      .mutate({ id: action.questionGroupId })
      .pipe(
        takeGraphQLResult(),
        map(res => {
          if (res?.toggleIcebreaker?.examQuestionGroup) {
            ctx.dispatch(
              new Act.SetIcebreakerSuccess(
                res?.toggleIcebreaker?.examQuestionGroup?.id
              )
            );
          }
        }),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetIcebreakerSuccess)
  public setIcebreakerSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { updatedExamQuestionId }: Act.SetIcebreakerSuccess
  ): void {
    const { data } = ctx.getState().items;
    assertIsDefined(data, 'Exam Question Data must be defined');
    assertIsDefined(updatedExamQuestionId, 'Exam Question Id must be defined');

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: {
          ...data,
          bookletA: {
            ...data.bookletA,
            examQuestionGroups: data.bookletA.examQuestionGroups.map(qg =>
              qg.id === updatedExamQuestionId
                ? { ...qg, icebreaker: qg.icebreaker === true ? false : true }
                : qg
            )
          },
          bookletB: {
            ...data.bookletB,
            examQuestionGroups: data.bookletB.examQuestionGroups.map(qg =>
              qg.id === updatedExamQuestionId
                ? { ...qg, icebreaker: qg.icebreaker === true ? false : true }
                : qg
            )
          }
        },
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant(
          'common.models.exam_questions_icebreaker'
        )
      })
    );
  }

  @Action(Act.PresortExamQuestions)
  public presortExamQuestions(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { presortInput }: Act.PresortExamQuestions
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.presortExamQuestionsService.mutate(presortInput).pipe(
      takeGraphQLResult(),
      switchMap(() =>
        ctx.dispatch(
          new Act.PresortExamQuestionsSuccess(presortInput.id.toString())
        )
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.PresortExamQuestionsSuccess)
  public presortExamQuestionsSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examId }: Act.PresortExamQuestionsSuccess
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    this.toasts.addSuccess(
      this.translate.instant(
        'exam_management.exam_questions.presort.success_message'
      )
    );

    ctx.dispatch([
      new LoadExam(examId),
      new Act.LoadExamQuestionGroups(examId)
    ]);
  }

  @Action(Act.RemoveExamQuestion)
  public removeExamQuestion(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examQuestionId, positionAandB }: Act.RemoveExamQuestion
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.removeExamQuestionService.mutate({ id: examQuestionId }).pipe(
      takeGraphQLResult(),
      switchMap(() =>
        ctx.dispatch(
          new Act.RemoveExamQuestionSuccess(examQuestionId, positionAandB)
        )
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.RemoveExamQuestionSuccess)
  public removeExamQuestionSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examQuestionId, positionAandB }: Act.RemoveExamQuestionSuccess
  ): void {
    const current = ctx.getState().items;
    const currentData = current.data;
    const newData = currentData
      ? {
          ...currentData,
          bookletA: {
            ...currentData.bookletA,
            examQuestionGroups: currentData.bookletA.examQuestionGroups.filter(
              qg => qg.id !== examQuestionId
            )
          },
          bookletB: {
            ...currentData.bookletB,
            examQuestionGroups: currentData.bookletB.examQuestionGroups.filter(
              qg => qg.id !== examQuestionId
            )
          }
        }
      : undefined;

    ctx.patchState({
      items: {
        ...current,
        data: newData,
        mutation: { state: 'success' }
      },
      selectedIds: []
    });

    ctx.dispatch(new Act.UpdatePositionsAfterDeletion(positionAandB));
  }

  @Action(Act.UpdatePositionsAfterDeletion)
  public updatePositionsAfterDeletion(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { positionAandB }: Act.UpdatePositionsAfterDeletion
  ): Observable<void> {
    void positionAandB;
    const currentData = ctx.getState().items.data;
    const bookletAQuestionGroups =
      currentData?.bookletA.examQuestionGroups ?? [];
    const bookletBQuestionGroups =
      currentData?.bookletB.examQuestionGroups ?? [];
    const updatedQuestionGroupPositions = this.formatPositionsAfterDeletion(
      bookletAQuestionGroups,
      bookletBQuestionGroups
    );

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to update positions');

    return this.updateEQGPositionsService
      .mutate({ poolId, newPositions: updatedQuestionGroupPositions })
      .pipe(
        takeGraphQLResult(),
        map(
          res =>
            res?.updateExamQuestionGroupPositions?.updatedExamQuestionsGroups
        ),
        filter(isDefined),
        switchMap(updatedExamQGs =>
          ctx.dispatch(
            new Act.UpdateExamQuestionGroupPositionSuccess(updatedExamQGs, true)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.UpdateExamQuestionVersion)
  public updateExamQuestionVersion(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examQuestionGroupId }: Act.UpdateExamQuestionVersion
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to update positions');

    return this.updateExamQuestionVersionService
      .mutate({ id: examQuestionGroupId.toString(), poolId })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.UpdateExamQuestionVersionSuccess(examQuestionGroupId)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.UpdateExamQuestionVersionSuccess)
  public updateExamQuestionVersionSuccess(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examQuestionGroupId }: Act.UpdateExamQuestionVersionSuccess
  ): void {
    const { data } = ctx.getState().items;
    assertIsDefined(data, 'Exam Question Data must be defined');

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: {
          ...data,
          bookletA: {
            ...data.bookletA,
            examQuestionGroups: data.bookletA.examQuestionGroups.map(qg =>
              qg.id === examQuestionGroupId
                ? { ...qg, isHeadVersion: true }
                : qg
            )
          },
          bookletB: {
            ...data.bookletB,
            examQuestionGroups: data.bookletB.examQuestionGroups.map(qg =>
              qg.id === examQuestionGroupId
                ? { ...qg, isHeadVersion: true }
                : qg
            )
          }
        },
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.question')
      })
    );
  }

  @Action(Act.UpdateExamQuestionGroupLockedState)
  public updateExamQuestionGroupLockedState(
    ctx: StateContext<ExamQuestionGroupsStateModel>,
    { examId, lockState }: Act.UpdateExamQuestionGroupLockedState
  ): void {
    const { data } = ctx.getState().items;
    if (data === undefined) return;

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: {
          ...data,
          bookletA: {
            ...data.bookletA,
            examQuestionGroups: data.bookletA.examQuestionGroups.map(qg =>
              qg.exam.id === examId && lockState === LockState.Locked
                ? { ...qg, locked: true }
                : { ...qg, locked: false }
            )
          },
          bookletB: {
            ...data.bookletB,
            examQuestionGroups: data.bookletB.examQuestionGroups.map(qg =>
              qg.exam.id === examId && lockState === LockState.Locked
                ? { ...qg, locked: true }
                : { ...qg, locked: false }
            )
          }
        },
        mutation: { state: 'success' }
      }
    });
  }

  private formatPositionsAfterDeletion(
    bookletAQuestionGroups: ExamQuestionGroup[],
    bookletBQuestionGroups: ExamQuestionGroup[]
  ): ExamQuestionGroupInput[] {
    return bookletAQuestionGroups.map(qg => {
      const positionA = bookletAQuestionGroups.findIndex(
        group => group.id === qg.id
      );
      const positionB = bookletBQuestionGroups.findIndex(
        group => group.id === qg.id
      );

      assertIsDefined(positionA, 'positionA must be defined');
      assertIsDefined(positionB, 'positionB must be defined');

      return { id: qg.id.toString(), positionA, positionB };
    });
  }

  private moveQuestionGroup(
    fromPosition: number,
    toPosition: number,
    questionGroups: ExamQuestionGroup[],
    selectedBooklet: BookletType
  ): ExamQuestionGroupInput[] {
    const rearrangedQuestionGroups = [...questionGroups];
    const len = rearrangedQuestionGroups.length;
    const from = Math.max(0, Math.min(fromPosition, len - 1));
    const to = Math.max(0, Math.min(toPosition, len - 1));

    const [moved] = rearrangedQuestionGroups.splice(from, 1);
    rearrangedQuestionGroups.splice(to, 0, moved);

    return rearrangedQuestionGroups.map((qg, index) => {
      return selectedBooklet === BookletType.A
        ? { id: qg.id.toString(), positionA: index }
        : { id: qg.id.toString(), positionB: index };
    });
  }
}
